import { medicalAidDisplay } from 'src/app/core/utils/scheme-code';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable } from 'rxjs';
import * as moment from 'moment';
import { formatDate, isPlatformBrowser } from '@angular/common';
import autoTable from 'jspdf-autotable';
import { jsPDF } from 'jspdf';
import * as QRCode from 'qrcode';
import { FULL_PAYMENT_STAMP, PARTIAL_PAYMENT_STAMP, PROBONO_PAYMENT_STAMP, RULE_TYPE_LABELS, YOCO_BASE64_IMAGE } from '../data/app.data';
import * as _ from 'lodash';
import { YocoService } from './yoco.service';
import { lineProvider, lineProviderKey, providerLabel } from '../utils/billing-line-provider';
import { DEFAULT_VAT_RATE, exclusiveOf, formatVatPercent, resolveVatRate, splitInclusive } from '../utils/vat.util';


import { environment } from './../../../environments/environment';
import { formatLineQuantity, lineQuantity } from '../utils/line-quantity';
@Injectable({
  providedIn: 'root'
})
export class PatientFinancalBillingService {
  private apiUrl = 'api';
  patientId?: string;
  group!: any;
  user!: any;

  contextPatient: any;
  contextUser: any;
  contextGroup: any;
  contextProvider: any;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: object,
    private yocoService: YocoService
  ) {
  }

  setContext({ patient, user, group, provider }: { patient: any; user: any; group: any; provider: any }) {
    this.contextPatient = patient;
    this.contextUser = user;
    this.contextGroup = group;
    this.contextProvider = provider;
  }

  clearContext() {
    this.contextPatient = null;
    this.contextUser = null;
    this.contextGroup = null;
    this.contextProvider = null;
  }

  getContext() {
    return {
      patient: this.contextPatient,
      user: this.contextUser,
      group: this.contextGroup,
      provider: this.contextProvider
    };
  }

  // The line item amounts stored on a billing document are VAT-inclusive. The items table
  // collapses from three columns to two in either of two independent cases:
  //   - vatExclusive (e.g. WCA): per-line values render as amount/(1+rate) and the VAT
  //     column is dropped because the amounts are already exclusive.
  //   - rate === 0: the practice is not a registered VAT vendor, so the document must not
  //     show VAT at all — not even as 0%.
  // Anything keyed to a column index must therefore branch on isTwoColumn(), never on
  // vatExclusive alone. Totals come from the authoritative inclusive totalDue via
  // splitInclusive(), so subtotal + vat reconciles to totalDue to the cent.
  private isTwoColumn(vatExclusive: boolean, vatRate: number): boolean {
    return vatExclusive || vatRate === 0;
  }

  private getItemsTableHead(vatExclusive: boolean, vatRate: number): string[][] {
    if (vatRate === 0) {
      return [['Details', 'Amount']];
    }
    return vatExclusive
      ? [['Details', 'Amount (excl.)']]
      : [['Details', 'VAT %', 'Amount (incl.)']];
  }

  private formatItemRow(detailsString: string, totalAmountInclusive: number, vatExclusive: boolean, vatRate: number): string[] {
    const inclusive = Number(totalAmountInclusive || 0);
    if (vatRate === 0) {
      return [detailsString, `R${inclusive.toFixed(2)}`];
    }
    if (vatExclusive) {
      return [detailsString, `R${exclusiveOf(inclusive, vatRate).toFixed(2)}`];
    }
    return [detailsString, formatVatPercent(vatRate), `R${inclusive.toFixed(2)}`];
  }

  private getItemsColumnStyles(vatExclusive: boolean, amountColWidth: number = 30, vatRate: number = DEFAULT_VAT_RATE): any {
    if (this.isTwoColumn(vatExclusive, vatRate)) {
      return {
        0: { halign: 'left' },
        1: { halign: 'center', cellWidth: amountColWidth },
      };
    }
    return {
      0: { halign: 'left' },
      1: { halign: 'center', cellWidth: 20 },
      2: { halign: 'center', cellWidth: amountColWidth },
    };
  }

  async generateBillingPDF(patientBillingDocumentId: string, billingData: any): Promise<Uint8Array> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('Not in browser platform, returning empty Uint8Array');
      return new Uint8Array();
    }


    // 1) Destructure and parse data
    const {
      patient,
      type,
      issueDate,
      dueDate,
      billingNumber,
      lineItems,
      totalDue: rawTotalDue,
      notes,
      group,
      user,
      ruleType,
      provider,
      providers,
      hospital,
      practiceInfo,
      vatExclusive: rawVatExclusive,
      vatRate: rawVatRate,
    } = billingData;
    const vatExclusive = rawVatExclusive === true;
    const vatRate = resolveVatRate(rawVatRate, group);

    //console.log("billingData", billingData);

    this.setContext({ patient: patient, user: user, group: group, provider: provider });

    // --- SORT lineItems: Check if this is a physiotherapy practice ---
    const isPhysiotherapyPractice = group?.groupSpeciality === 'Physiotherapy';
    
    let sortedLineItems;
    if (isPhysiotherapyPractice) {
      // For physiotherapy practices, use displayIndex for custom ordering
      // Keep existing order but ensure consumables are last
      sortedLineItems = _.orderBy(lineItems, [
        (item) => item.isModifierLine ? 1 : 0, // First sort by isModifierLine (0 comes before 1)
        (item) => item.displayIndex !== undefined ? item.displayIndex : 999, // Then by displayIndex
        (item) => item.date ? moment(item.date).valueOf() : 0, // Then by date as fallback
        (item) => -(item.units || item.quantity || 0), // Then by units descending
        (item) => item.codeType === 'consumable' ? 1 : 0 // Finally ensure consumables are last
      ]);
    } else {
      // For other practices, sort by codeType (procedures first, consumables last)
      sortedLineItems = _.orderBy(lineItems, [
        (item) => item.isModifierLine ? 1 : 0, // First sort by isModifierLine (0 comes before 1)
        (item) => item.codeType === 'consumable' ? 1 : 0, // Then by codeType (procedures first, consumables last)
        (item) => item.date ? moment(item.date).valueOf() : 0, // Then by date
        (item) => -(item.units || item.quantity || 0) // Then by units descending (negative for descending)
      ]);
    }

    // Group line items by date (use sortedLineItems instead of lineItems)
    const groupedItems: { [key: string]: any[] } = sortedLineItems.reduce((acc: { [key: string]: any[] }, item: any) => {
      const date = moment(item.date).format('DD/MM/YYYY');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(item);
      return acc;
    }, {});

    // --- merge duplicates in each date bucket ---
    // For physiotherapy practices: DO NOT merge items with same tariff code (keep all items separate)
    // For other practices: merge items with the same code + unit-price
    Object.keys(groupedItems).forEach(date => {
      const items = groupedItems[date];
      
      if (isPhysiotherapyPractice) {
        // For physiotherapy practices, do not merge - keep all items separate
        groupedItems[date] = items;
      } else {
        // For other practices, merge items with the same code + unit-price
        const merged = Object.values(
          items.reduce((acc: Record<string, any>, item: any) => {
            const qty = item.quantity || 0;
            const total = item.totalAmount || item.balance || 0;
            // compute unit-price (guards against qty===0)
            const unitPrice = qty > 0 ? total / qty : 0;
            // Lines billed for different providers stay apart.
            const key = `${item.code}-${unitPrice.toFixed(2)}-${lineProviderKey(item, provider, providers)}`;

            if (!acc[key]) {
              // clone first occurrence
              acc[key] = { ...item };
            } else {
              // bump up qty and total
              acc[key].quantity = lineQuantity(acc[key].quantity + qty);
              acc[key].balance += total;
              acc[key].totalAmount += total;
            }
            return acc;
          }, {})
        );

        // overwrite with merged array
        groupedItems[date] = merged;
      }
    });

    // 2) Calculate financials
    const totalDue = Number(rawTotalDue) || 0;
    // totalDue is VAT-inclusive, so VAT is extracted with rate/(1+rate) — R115 incl. at
    // 15% is R100.00 + R15.00. At a 0% rate this yields the full amount with no VAT.
    const { subtotal: subTotal, vat } = splitInclusive(totalDue, vatRate);

    // 3) Init jsPDF
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
    const prefix = type === 'Invoice' ? 'INV' : 'QUO';
    const margin = 10;
    //const letterBar = 10;                         // your "letterhead" bar height
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    // 4) QR code
    let qrImageBase64 = '';
    if (type === 'Invoice') {
      try {
        const cents = Math.round(totalDue * 100);
        //console.log("Generating YOCO Checkout URL for ", cents);
        let url = await this.generateYocoCheckoutUrl(cents, patientBillingDocumentId);
        //console.log("YOCO Checkout URL: ", url);
        qrImageBase64 = await QRCode.toDataURL(url, { scale: 2 });
      } catch (error) {
        console.error("Error generating YOCO Checkout URL: ", error);
        //const url = `https://yoco.com/pay/invoice/abcdefgs`;
        //qrImageBase64 = await QRCode.toDataURL(url, { scale: 2 });
      }
    }

    // 5) Fetch images & footer text
    const logoImage = await this.getBase64ImageFromURL(group?.headerImageUrl);
    const footerImage = group?.footerImageUrl
      ? await this.getBase64ImageFromURL(group.footerImageUrl)
      : null;
    const now = formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss', 'en-US');
    const footerText = `Generated by ${user.title} ${user.name} ${user.surname} on ${now}`;

    // 6) Build header/footer handlers
    const headerHeight = 35;  // must match what you pass into createHeaderFunction
    //const footerHeight = 33;  // ditto
    const footerHeight = 10;  // ditto
    const headerFunction = await this.createHeaderFunction(doc, logoImage, headerHeight);
    //const footerFunction = await this.createFooterFunction(doc, footerImage, footerText, footerHeight);
    const footerFunction = await this.createFooterFunction(doc, null, footerText, footerHeight);

    // 7) Draw bar + logo
    await headerFunction();


    // 8) Safe content area
    const safeTop = headerHeight + 10;
    const safeBottom = pageH - footerHeight - margin;
    let y = safeTop;

    // 9) Title & meta
    doc.setFont('helvetica', 'bold').setFontSize(14);
    doc.text(`${type === 'Invoice' ? 'TAX INVOICE' : 'ESTIMATE'}`, pageW / 2, y, { align: 'center' });
    y += 8;

    // Calculate column widths and positions
    const colWidth = (pageW - (margin * 2)) / 3;

    // Title & Meta Table
    autoTable(doc, {
      startY: y,
      body: [
        [
          { content: `${type} Number: ${billingNumber}`, styles: { halign: 'left' } },
          { content: `Date: ${moment(issueDate).format('DD/MM/YYYY')}`, styles: { halign: 'center' } },
          { content: `Due: ${moment(dueDate).format('DD/MM/YYYY')}`, styles: { halign: 'right' } }
        ]
      ],
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 0,
        lineColor: [255, 255, 255],
        lineWidth: 0
      },
      columnStyles: {
        0: { cellWidth: colWidth },
        1: { cellWidth: colWidth },
        2: { cellWidth: colWidth }
      },
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2
    });

    y += 10;
    
    // 10) Two-column panel & divider
    const panelH = 45;
    doc.setDrawColor(200)
      .line(pageW / 2, y, pageW / 2, y + panelH);

    // Patient and Practice Info Table
    autoTable(doc, {
      startY: y,
      body: this.createDocumentDetailsHeaderRow(patient, practiceInfo, group, vatRate),
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 1,
        lineColor: [255, 255, 255],
        lineWidth: 0,
        minCellHeight: 6
      },
      columnStyles: {
        0: { cellWidth: 30, halign: 'left' },  // Label
        1: { cellWidth: 60, halign: 'left' },  // Value
        2: { cellWidth: 30, halign: 'left', cellPadding: { left: 10, right: 0, top: 0, bottom: 0 } },  // Label with extra left padding
        3: { cellWidth: 60, halign: 'left', cellPadding: { left: 0, right: 0, top: 0, bottom: 0 } }   // Value
      },
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2
    });

    y += panelH + 5;

    // 11) Items table
    // Create table body with date headers
    const tableBody = Object.entries(groupedItems).flatMap(([date, items]) => {
      // Add date header row with custom styling
      const providerName = `${provider.title} ${provider.name} ${provider.surname}`
      const headerRow = this.createPatientAndProviderRow(date, patient, providerName, group, provider);

      // Add items for this date
      const itemRows = items.map((i: any) => {

        const icdCodes = i.icdCodes && i.icdCodes?.length > 0 ? i.icdCodes.map((code: any) => code.code).join(', ') : '-';
        const pmbCodes = i.icdCodes && i.icdCodes?.length > 0
          ? i.icdCodes
            .flatMap((code: any) => code.pmbCodes || [])
            .map((pmb: any) => pmb.code)
            .filter((code: string) => code)
            .join(', ')
          : '-';
        const modifiers = i.modifiers && i.modifiers.length > 0 ? i.modifiers.join(', ') : '-';

        // Format the details string
        const quantityStr = formatLineQuantity(i);
        const details = [
          `${i.code || '-'} x ${quantityStr} - ${this.truncateDescription(i.description || '-', 150)}`,
        ]

        if (modifiers !== '-') {
          details.push(`Modifiers: ${modifiers}`);
        }

        if (i.startTime && i.endTime) {
          details.push(`Time: ${i.startTime} - ${i.endTime}`);
        }

        if (i.weight || i.height || i.BMI) {
          details.push(`Weight: ${i.weight || '-'}kg, Height: ${i.height || '-'}cm, BMI: ${i.BMI || '-'}`);
        }

        const usingRule = i.ruleTypeOverride || ruleType
        const ruleTypeLabel = RULE_TYPE_LABELS[usingRule];
        const hospitalName = hospital && usingRule.indexOf('IH') > -1 ? hospital.name : '';
        details.push(`Place of service: ${ruleTypeLabel} ${hospitalName ? ' - ' + hospitalName : ''}`,)

        const lineProviderName = providerLabel(lineProvider(i, provider, providers));
        if (lineProviderName) {
          details.push(`Provider: ${lineProviderName}`);
        }

        if (icdCodes !== '-') {
          details.push(`ICD-10 Codes: ${icdCodes}`);
        }

        if (pmbCodes && pmbCodes !== '-') {
          details.push(`PMB Codes: ${pmbCodes}`);
        }

        const detailsString = details.join('\n');

        //console.log("i", i);

        return this.formatItemRow(detailsString, Number(i.totalAmount || i.balance || 0), vatExclusive, vatRate);
      });

      return [...headerRow, ...itemRows];
      //return [...itemRows];
    });

    autoTable(doc, {
      startY: y,
      head: this.getItemsTableHead(vatExclusive, vatRate),
      body: tableBody,
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
        fillColor: [255, 255, 255],
        lineColor: [255, 255, 255],
        lineWidth: 0
      },
      headStyles: {
        fillColor: [225, 225, 225],
        //fillColor: [0, 150, 136],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: this.getItemsColumnStyles(vatExclusive, 30, vatRate),
      margin: { top: headerHeight + 10, bottom: footerHeight + 10, left: margin, right: margin },
      tableWidth: pageW - margin * 2,
      didDrawPage: data => { y = data.cursor!.y; }
    });

    // --- Before rendering summary/QR/notes/totals row and banking details, check space ---
    const bottomSectionHeight = 40 + 20 + 10; // summary row + banking details + buffer
    if (safeBottom - y < bottomSectionHeight) {
      doc.addPage();
      y = safeTop;
    }

    // Ensure yocoImgBase64 is defined
    const yocoImgBase64 = YOCO_BASE64_IMAGE;

    // --- Render summary/QR/notes/totals row in 3 columns ---
    // Define block sizes
    const qrSize = 30;
    const yocoImgW = 30;
    const yocoImgH = 10;
    const scanToPayFontSize = 8;
    const scanToPayGap = 2; // gap between Yoco image and Scan to Pay
    const yocoBlockH = yocoImgH + scanToPayGap + scanToPayFontSize + 2; // Yoco image + gap + Scan to Pay text
    const blockH = Math.max(qrSize, yocoBlockH);
    const blockY = safeBottom - 38 - blockH / 2; // center block above banking details
    const qrX = margin;
    const qrY = blockY;
    const yocoX = qrX + qrSize + 2; // 2mm gap after QR
    const yocoY = blockY + (blockH - yocoBlockH) / 2;
    const scanToPayY = yocoY + yocoImgH + scanToPayGap + scanToPayFontSize;

    // Draw QR code
    if (qrImageBase64) {
      doc.addImage(qrImageBase64, 'PNG', qrX, qrY, qrSize, qrSize);

      // Draw Yoco image
      doc.addImage(yocoImgBase64, 'JPG', yocoX, yocoY + 5, yocoImgW, yocoImgH);
      // Draw Scan to Pay text
      doc.setFont('helvetica', 'bold').setFontSize(scanToPayFontSize)
        .text('Scan to Pay', yocoX + yocoImgW / 2, scanToPayY, { align: 'center' });
    }

    // Notes block: center vertically with QR/Yoco block
    if (notes) {
      const notesX = yocoX + yocoImgW + 5; // 5mm gap after Yoco image
      // Calculate notesY so first line is vertically centered with the block
      doc.setFont('helvetica', 'bold').setFontSize(9);
      const notesLabel = 'Notes:';
      const notesLabelHeight = 4;
      const notesFontSize = 8;
      const split = doc.splitTextToSize(notes, 60);
      const notesBlockH = notesLabelHeight + split.length * notesFontSize + 2;
      const notesY = blockY + (blockH - notesBlockH) / 2 + notesLabelHeight;
      doc.text(notesLabel, notesX, notesY);
      doc.setFont('helvetica', 'normal').setFontSize(notesFontSize);
      doc.text(split, notesX, notesY + 4);
    }

    // Column 3: Totals (labels and values in two columns, right-aligned)
    const colW = (pageW - margin * 2) / 3;
    const totalsX = margin + 2 * colW;
    let totalsY = blockY + 8;
    const labelW = 22;
    const valueW = 28;
    // A practice that is not a registered VAT vendor may not issue anything resembling a
    // tax invoice, so at a 0% rate the Subtotal and VAT lines are omitted entirely rather
    // than printed as zero — the subtotal would equal the total anyway.
    if (vatRate > 0) {
      doc.setFont('helvetica', 'bold').setFontSize(8)
        .text(vatExclusive ? 'Subtotal (excl.):' : 'Subtotal:', totalsX + labelW, totalsY, { align: 'right' });
      doc.setFont('helvetica', 'normal').setFontSize(8)
        .text(`R${subTotal.toFixed(2)}`, totalsX + labelW + valueW, totalsY, { align: 'right' });
      totalsY += 8;
      doc.setFont('helvetica', 'bold').setFontSize(8)
        .text('VAT:', totalsX + labelW, totalsY, { align: 'right' });
      doc.setFont('helvetica', 'normal').setFontSize(8)
        .text(`R${vat.toFixed(2)}`, totalsX + labelW + valueW, totalsY, { align: 'right' });
      totalsY += 8;
    }
    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(200, 0, 0)
      .text('Total Due:', totalsX + labelW, totalsY, { align: 'right' });
    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(200, 0, 0)
      .text(`R${totalDue.toFixed(2)}`, totalsX + labelW + valueW, totalsY, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    // --- Banking details heading and table just above the footer ---
    const bankingTableHeight = 13; // Reduce height for less gap
    const bankingY = safeBottom - bankingTableHeight;
    doc.setFont('helvetica', 'bold').setFontSize(9);
    doc.text('Banking Details', margin, bankingY - 2); // Reduce gap above table
    const banking = group.bankingDetails || {};
    autoTable(doc, {
      startY: bankingY,
      body: [
        [
          { content: 'Account name:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.accountName || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Bank:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.bankName || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Branch:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.branchName || 'N/A', styles: { halign: 'left', fontSize: 8 } }
        ],
        [
          { content: 'Branch code:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.branchCode || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Account no.:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.accountNumber || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Account type:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.accountType || 'N/A', styles: { halign: 'left', fontSize: 8 } }
        ],
        [
          { content: 'Payment reference:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: `${patient.patientNumber}`, styles: { halign: 'left', fontSize: 8 } },
          '', '', '', ''
        ]
      ],
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 0.5,
        lineColor: [255, 255, 255],
        lineWidth: 0,
        minCellHeight: 4,
        valign: 'top',
        overflow: 'linebreak',
      },
      columnStyles: {
        0: { cellWidth: (pageW - margin * 2) / 6 },
        1: { cellWidth: (pageW - margin * 2) / 6 },
        2: { cellWidth: (pageW - margin * 2) / 6 },
        3: { cellWidth: (pageW - margin * 2) / 6 },
        4: { cellWidth: (pageW - margin * 2) / 6 },
        5: { cellWidth: (pageW - margin * 2) / 6 },
      },
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2,
      didDrawCell: (data) => {
        data.cell.styles.fillColor = [225, 225, 225]; // subtle light gray
      }
    });

    // 16) Render header/footer on every page
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      await headerFunction();
      await footerFunction(i, totalPages);
    }

    return new Uint8Array(doc.output('arraybuffer'));
  }


  async generateReceiptPDF(patientBillingDocumentId: string, paymentData: any): Promise<Uint8Array> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('Not in browser platform, returning empty Uint8Array');
      return new Uint8Array();
    }

    // 1) Destructure and parse data
    const {
      billingNumber,
      patientName,
      patient,
      paymentDate,
      paymentMethod,
      amountRecieved,
      lineItems,
      payments,
      notes,
      practiceInfo,
      user,
      group,
      provider,
      hospital,
      vatExclusive: rawVatExclusive,
      vatRate: rawVatRate,
    } = paymentData;
    const vatExclusive = rawVatExclusive === true;
    const vatRate = resolveVatRate(rawVatRate, group);
    const twoColumn = this.isTwoColumn(vatExclusive, vatRate);

    this.setContext({ patient: patient, user: user, group: group, provider: provider });

    // 2) Calculate financials
    // Use outstandingBalance from paymentData if provided (from billing document), otherwise calculate it
    const originalTotalDue = lineItems.reduce((sum: number, item: any) => sum + Number(item.totalAmount || item.balance || 0), 0);
    const paymentAmount = Number(amountRecieved) || 0;
    const outstandingBalance = paymentData.outstandingBalance !== undefined 
      ? Number(paymentData.outstandingBalance) 
      : Math.max(0, originalTotalDue - paymentAmount);
    const isPartiallyPaid = outstandingBalance > 0;

    // 3) Init jsPDF
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
    const margin = 10;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    // 4) QR code - only show if partially paid and there's outstanding balance
    let qrImageBase64 = '';
    if (isPartiallyPaid && outstandingBalance > 0) {
      try {
        const cents = Math.round(outstandingBalance * 100);
        //console.log("Generating YOCO Checkout URL for outstanding balance: ", cents);
        let url = await this.generateYocoCheckoutUrl(cents, patientBillingDocumentId);
        //console.log("YOCO Checkout URL: ", url);
        qrImageBase64 = await QRCode.toDataURL(url, { scale: 2 });
      } catch (error) {
        console.error("Error generating YOCO Checkout URL: ", error);
      }
    }

    // 5) Fetch images & footer text
    const logoImage = await this.getBase64ImageFromURL(group?.headerImageUrl);
    const footerImage = group?.footerImageUrl
      ? await this.getBase64ImageFromURL(group.footerImageUrl)
      : null;
    const now = formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss', 'en-US');
    const footerText = `Generated by ${user.title} ${user.name} ${user.surname} on ${now}`;

    // 6) Build header/footer handlers
    const headerHeight = 35;
    const footerHeight = 10;
    const headerFunction = await this.createHeaderFunction(doc, logoImage, headerHeight);
    const footerFunction = await this.createFooterFunction(doc, null, footerText, footerHeight);

    // 7) Draw header
    await headerFunction();

    // 8) Safe content area
    const safeTop = headerHeight + 10;
    const safeBottom = pageH - footerHeight - margin;
    let y = safeTop;

    // 9) Title & meta
    doc.setFont('helvetica', 'bold').setFontSize(14);
    doc.text('PAYMENT RECEIPT', pageW / 2, y, { align: 'center' });
    y += 8;

    // Calculate column widths and positions
    const colWidth = (pageW - (margin * 2)) / 3;

    // Title & Meta Table
    autoTable(doc, {
      startY: y,
      body: [
        [
          { content: `Invoice Number: ${billingNumber}`, styles: { halign: 'left' } },
          { content: `Payment Date: ${moment(paymentDate).format('DD/MM/YYYY')}`, styles: { halign: 'center' } },
          { content: `Method: ${paymentMethod}`, styles: { halign: 'right' } }
        ]
      ],
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 0,
        lineColor: [255, 255, 255],
        lineWidth: 0
      },
      columnStyles: {
        0: { cellWidth: colWidth },
        1: { cellWidth: colWidth },
        2: { cellWidth: colWidth }
      },
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2
    });

    y += 10;

    // 10) Two-column panel & divider
    const panelH = 45;
    doc.setDrawColor(200)
      .line(pageW / 2, y, pageW / 2, y + panelH);

    // Patient and Practice Info Table
    autoTable(doc, {
      startY: y,
      body: this.createDocumentDetailsHeaderRow(patient, practiceInfo, group, vatRate),
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 1,
        lineColor: [255, 255, 255],
        lineWidth: 0,
        minCellHeight: 6
      },
      columnStyles: {
        0: { cellWidth: 30, halign: 'left' },
        1: { cellWidth: 60, halign: 'left' },
        2: { cellWidth: 30, halign: 'left', cellPadding: { left: 10, right: 0, top: 0, bottom: 0 } },
        3: { cellWidth: 60, halign: 'left', cellPadding: { left: 0, right: 0, top: 0, bottom: 0 } }
      },
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2
    });

    y += panelH + 5;

    // 11) Items table - include original line items plus payment line
    const tableBody = [];

    // --- SORT lineItems: Check if this is a physiotherapy practice ---
    const isPhysiotherapyPractice = group?.groupSpeciality === 'Physiotherapy';
    
    let sortedLineItems;
    if (isPhysiotherapyPractice) {
      // For physiotherapy practices, use displayIndex for custom ordering
      // Keep existing order but ensure consumables are last
      sortedLineItems = _.orderBy(lineItems, [
        (item) => item.isModifierLine ? 1 : 0, // First sort by isModifierLine (0 comes before 1)
        (item) => item.displayIndex !== undefined ? item.displayIndex : 999, // Then by displayIndex
        (item) => item.date ? moment(item.date).valueOf() : 0, // Then by date as fallback
        (item) => -(item.units || item.quantity || 0), // Then by units descending
        (item) => item.codeType === 'consumable' ? 1 : 0 // Finally ensure consumables are last
      ]);
    } else {
      // For other practices, sort by codeType (procedures first, consumables last)
      sortedLineItems = _.orderBy(lineItems, [
        (item) => item.isModifierLine ? 1 : 0, // First sort by isModifierLine (0 comes before 1)
        (item) => item.codeType === 'consumable' ? 1 : 0, // Then by codeType (procedures first, consumables last)
        (item) => item.date ? moment(item.date).valueOf() : 0, // Then by date
        (item) => -(item.units || item.quantity || 0) // Then by units descending (negative for descending)
      ]);
    }

    // Group line items by date (use sortedLineItems instead of lineItems)
    const groupedItems: { [key: string]: any[] } = sortedLineItems.reduce((acc: { [key: string]: any[] }, item: any) => {
      const date = moment(item.date).format('DD/MM/YYYY');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(item);
      return acc;
    }, {});

    // Add original line items with date headers
    Object.entries(groupedItems).forEach(([date, items]) => {
      const providerName = `${provider.title} ${provider.name} ${provider.surname}`;
      const headerRow = this.createPatientAndProviderRow(date, patient, providerName, group, provider);

      // Add items for this date
      const itemRows = items.map((item: any) => {

        /* 
        const icdCodes = i.icdCodes && i.icdCodes?.length > 0 ? i.icdCodes.map((code: any) => code.code).join(', ') : '-';
        const pmbCodes = i.icdCodes && i.icdCodes?.length > 0
          ? i.icdCodes
            .flatMap((code: any) => code.pmbCodes || [])
            .map((pmb: any) => pmb.code)
            .filter((code: string) => code)
            .join(', ')
          : '-';
        const modifiers = i.modifiers && i.modifiers.length > 0 ? i.modifiers.join(', ') : '-';

        // Format the details string
        const details = [
          `${i.code || '-'} x ${formatLineQuantity(i)} - ${this.truncateDescription(i.description || '-', 150)}`,
        ]

        if (modifiers !== '-') {
          details.push(`Modifiers: ${modifiers}`);
        }

        if (i.startTime && i.endTime) {
          details.push(`Time: ${i.startTime} - ${i.endTime}`);
        }

        if (i.weight || i.height || i.BMI) {
          details.push(`Weight: ${i.weight || '-'}kg, Height: ${i.height || '-'}cm, BMI: ${i.BMI || '-'}`);
        }

        const usingRule = i.ruleTypeOverride || ruleType
        const ruleTypeLabel = RULE_TYPE_LABELS[usingRule];
        const hospitalName = hospital && usingRule.indexOf('IH') > -1 ? hospital.name : '';
        details.push(`Place of service: ${ruleTypeLabel} ${hospitalName ? ' - ' + hospitalName : ''}`,)

        if (icdCodes !== '-') {
          details.push(`ICD-10 Codes: ${icdCodes}`);
        }

        if (pmbCodes && pmbCodes !== '-') {
          details.push(`PMB Codes: ${pmbCodes}`);
        }

        const detailsString = details.join('\n');


        return [
          detailsString,
          '15%',
          `R${Number(i.balance || 0).toFixed(2)}`
        ];
        
        */

        const icdCodes = item.icdCodes && item.icdCodes?.length > 0 ? item.icdCodes.map((code: any) => code.code).join(', ') : '-';
        const pmbCodes = item.icdCodes && item.icdCodes?.length > 0
          ? item.icdCodes
            .flatMap((code: any) => code.pmbCodes || [])
            .map((pmb: any) => pmb.code)
            .filter((code: string) => code)
            .join(', ')
          : '-';
        const modifiers = item.modifiers && item.modifiers.length > 0 ? item.modifiers.join(', ') : '-';

        // Format the details string
        const quantityStr = formatLineQuantity(item);
        const details = [
          `${item.code || '-'} x ${quantityStr} - ${this.truncateDescription(item.description || '-', 150)}`
        ];

        if (modifiers !== '-') {
          details.push(`Modifiers: ${modifiers}`);
        }

        if (item.startTime && item.endTime) {
          details.push(`Time: ${item.startTime} - ${item.endTime}`);
        }

        if (item.weight || item.height || item.BMI) {
          details.push(`Weight: ${item.weight || '-'}kg, Height: ${item.height || '-'}cm, BMI: ${item.BMI || '-'}`);
        }

        //const usingRule = item.ruleTypeOverride;
        //const ruleTypeLabel = RULE_TYPE_LABELS[usingRule];
        //const hospitalName = hospital && usingRule?.indexOf('IH') > -1 ? hospital.name : '';
        //details.push(`Place of service: ${ruleTypeLabel} ${hospitalName ? ' - ' + hospitalName : ''}`,)

        const lineProviderName = providerLabel(lineProvider(item, provider));
        if (lineProviderName) {
          details.push(`Provider: ${lineProviderName}`);
        }

        if (icdCodes !== '-') {
          details.push(`ICD-10 Codes: ${icdCodes}`);
        }

        if (pmbCodes && pmbCodes !== '-') {
          details.push(`PMB Codes: ${pmbCodes}`);
        }

        const detailsString = details.join('\n');

        return this.formatItemRow(detailsString, Number(item.totalAmount || item.balance || 0), vatExclusive, vatRate);
      });

      tableBody.push(...headerRow, ...itemRows);
    });

    // Add payment line — payment amounts stay as the inclusive figure regardless of mode
    const paymentRow = twoColumn
      ? [
          `Payment received on ${moment(paymentDate).format('DD/MM/YYYY')} via ${paymentMethod}`,
          `- R${paymentAmount.toFixed(2)}`,
        ]
      : [
          `Payment received on ${moment(paymentDate).format('DD/MM/YYYY')} via ${paymentMethod}`,
          '',
          `- R${paymentAmount.toFixed(2)}`,
        ];
    tableBody.push(paymentRow);

    const amountColumnIndex = twoColumn ? 1 : 2;
    autoTable(doc, {
      startY: y,
      head: this.getItemsTableHead(vatExclusive, vatRate),
      body: tableBody,
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
        fillColor: [255, 255, 255],
        lineColor: [255, 255, 255],
        lineWidth: 0
      },
      headStyles: {
        fillColor: [225, 225, 225],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: this.getItemsColumnStyles(vatExclusive, 30, vatRate),
      margin: { top: headerHeight + 10, bottom: footerHeight + 10, left: margin, right: margin },
      tableWidth: pageW - margin * 2,
      didParseCell: (data) => {
        // only look at the body section, and only the first column (Details)
        if (data.section === 'body' && data.column.index === 0) {
          // join cell.text (an array of lines) into one string
          const fullText = data.cell.text.join(' ');
          if (fullText.includes('Payment received')) {
            // set the RGB textColor to green
            data.cell.styles.textColor = [0, 128, 0];
            data.cell.styles.fontStyle = 'bold';
          }
        }
        if (data.section === 'body' && data.column.index === amountColumnIndex) {
          // join cell.text (an array of lines) into one string
          const fullText = data.cell.text.join(' ');
          if (fullText.includes('- R')) {
            // set the RGB textColor to green
            data.cell.styles.textColor = [0, 128, 0];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      didDrawPage: data => { y = data.cursor!.y; }
    });

    // --- Before rendering summary/QR/notes/totals row and banking details, check space ---
    const bottomSectionHeight = 40 + 20 + 10;
    if (safeBottom - y < bottomSectionHeight) {
      doc.addPage();
      y = safeTop;
    }

    // Ensure yocoImgBase64 is defined
    const yocoImgBase64 = YOCO_BASE64_IMAGE;

    // --- Render summary/QR/notes/totals row in 3 columns ---
    const qrSize = 30;
    const yocoImgW = 30;
    const yocoImgH = 10;
    const scanToPayFontSize = 8;
    const scanToPayGap = 2;
    const yocoBlockH = yocoImgH + scanToPayGap + scanToPayFontSize + 2;
    const blockH = Math.max(qrSize, yocoBlockH);
    const blockY = safeBottom - 38 - blockH / 2;
    const qrX = margin;
    const qrY = blockY;
    const yocoX = qrX + qrSize + 2;
    const yocoY = blockY + (blockH - yocoBlockH) / 2;
    const scanToPayY = yocoY + yocoImgH + scanToPayGap + scanToPayFontSize;

    // Draw QR code only if partially paid
    if (qrImageBase64 && isPartiallyPaid) {
      doc.addImage(qrImageBase64, 'PNG', qrX, qrY, qrSize, qrSize);

      // Draw Yoco image
      doc.addImage(yocoImgBase64, 'JPG', yocoX, yocoY + 5, yocoImgW, yocoImgH);
      // Draw Scan to Pay text
      doc.setFont('helvetica', 'bold').setFontSize(scanToPayFontSize)
        .text('Scan to Pay', yocoX + yocoImgW / 2, scanToPayY, { align: 'center' });
    }

    // Notes block: center vertically with QR/Yoco block
    if (notes) {
      const notesX = yocoX + yocoImgW + 5;
      doc.setFont('helvetica', 'bold').setFontSize(9);
      const notesLabel = 'Notes:';
      const notesLabelHeight = 4;
      const notesFontSize = 8;
      const split = doc.splitTextToSize(notes, 60);
      const notesBlockH = notesLabelHeight + split.length * notesFontSize + 2;
      const notesY = blockY + (blockH - notesBlockH) / 2 + notesLabelHeight;
      doc.text(notesLabel, notesX, notesY);
      doc.setFont('helvetica', 'normal').setFontSize(notesFontSize);
      doc.text(split, notesX, notesY + 4);
    }

    // Column 3: Totals (labels and values in two columns, right-aligned)
    const colW = (pageW - margin * 2) / 3;
    const totalsX = margin + 2 * colW;
    let totalsY = blockY + 8;
    const labelW = 22;
    const valueW = 28;

    doc.setFont('helvetica', 'bold').setFontSize(8)
      .text('Original Total:', totalsX + labelW, totalsY, { align: 'right' });
    doc.setFont('helvetica', 'normal').setFontSize(8)
      .text(`R${originalTotalDue.toFixed(2)}`, totalsX + labelW + valueW, totalsY, { align: 'right' });
    totalsY += 8;

    /* doc.setFont('helvetica', 'bold').setFontSize(8)
      .text('Payment Received:', totalsX + labelW, totalsY, { align: 'right' });
    doc.setFont('helvetica', 'normal').setFontSize(8)
      .text(`- R${paymentAmount.toFixed(2)}`, totalsX + labelW + valueW, totalsY, { align: 'right' }); */

    // now colour the amount green and make it bold
    doc.setTextColor(0, 128, 0);
    doc.setFont('helvetica', 'bold').setFontSize(8)
      .text('Payment Received:', totalsX + labelW, totalsY, { align: 'right' });
    doc.setFont('helvetica', 'bold').setFontSize(8)
      .text(`- R${paymentAmount.toFixed(2)}`, totalsX + labelW + valueW, totalsY, { align: 'right' });

    // reset back to black and normal weight for anything that follows
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');

    totalsY += 8;

    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(200, 0, 0)
      .text('Outstanding Balance:', totalsX + labelW, totalsY, { align: 'right' });
    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(200, 0, 0)
      .text(`R${outstandingBalance.toFixed(2)}`, totalsX + labelW + valueW, totalsY, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    // --- Banking details heading and table just above the footer ---
    const bankingTableHeight = 13;
    const bankingY = safeBottom - bankingTableHeight;
    doc.setFont('helvetica', 'bold').setFontSize(9);
    doc.text('Banking Details', margin, bankingY - 2);
    const banking = group.bankingDetails || {};
    autoTable(doc, {
      startY: bankingY,
      body: [
        [
          { content: 'Account name:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.accountName || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Bank:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.bankName || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Branch:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.branchName || 'N/A', styles: { halign: 'left', fontSize: 8 } }
        ],
        [
          { content: 'Branch code:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.branchCode || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Account no.:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.accountNumber || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Account type:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.accountType || 'N/A', styles: { halign: 'left', fontSize: 8 } }
        ],
        [
          { content: 'Payment reference:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: `${patient.patientNumber}`, styles: { halign: 'left', fontSize: 8 } },
          '', '', '', ''
        ]
      ],
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 0.5,
        lineColor: [255, 255, 255],
        lineWidth: 0,
        minCellHeight: 4,
        valign: 'top',
        overflow: 'linebreak',
      },
      columnStyles: {
        0: { cellWidth: (pageW - margin * 2) / 6 },
        1: { cellWidth: (pageW - margin * 2) / 6 },
        2: { cellWidth: (pageW - margin * 2) / 6 },
        3: { cellWidth: (pageW - margin * 2) / 6 },
        4: { cellWidth: (pageW - margin * 2) / 6 },
        5: { cellWidth: (pageW - margin * 2) / 6 },
      },
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2,
      didDrawCell: (data) => {
        data.cell.styles.fillColor = [225, 225, 225];
      }
    });

    // --- Add payment stamp based on payment amount and payment method ---
    const isFullPayment = Math.abs(paymentAmount - originalTotalDue) < 0.01; // Account for floating point precision
    const isProBonoPayment = paymentMethod && (
      paymentMethod.toLowerCase().includes('pro bono') || 
      paymentMethod.toLowerCase().includes('pro-bono') || 
      paymentMethod.toLowerCase().includes('probono')
    );
    
    let stampImage: string;
    if (isProBonoPayment) {
      stampImage = PROBONO_PAYMENT_STAMP;
    } else if (isFullPayment) {
      stampImage = FULL_PAYMENT_STAMP;
    } else {
      stampImage = PARTIAL_PAYMENT_STAMP;
    }

    // Position the stamp - use QR position if QR code isn't added, otherwise use current position
    const stampSize = 25; // Stamp image size
    let stampX: number;
    let stampY: number;

    if (qrImageBase64) {
      // QR code is present, use current position (top-right)
      stampX = pageW - margin - stampSize - 15; // Right margin
      stampY = safeTop + 20; // Below the title
    } else {
      // QR code is not present, use QR code position
      stampX = margin; // Left margin (QR position)
      stampY = blockY; // Same Y as QR code
    }

    // Add the payment stamp
    doc.addImage(stampImage, 'PNG', stampX, stampY, stampSize, stampSize);

    // 16) Render header/footer on every page
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      await headerFunction();
      await footerFunction(i, totalPages);
    }

    return new Uint8Array(doc.output('arraybuffer'));
  }

  async generateStatementPDF(statementData: any): Promise<Uint8Array> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('Not in browser platform, returning empty Uint8Array');
      return new Uint8Array();
    }

    // 1) Destructure and parse data
    const {
      startDate,
      endDate,
      patient,
      transactions,
      billingNumber,
      practiceInfo,
      subTotal,
      vat,
      totalDue,
      group,
      user,
      agingSummary,
      vatExclusive: rawVatExclusive,
      vatRate: rawVatRate,
    } = statementData;
    const vatExclusive = rawVatExclusive === true;
    const vatRate = resolveVatRate(rawVatRate, group);
    const twoColumn = this.isTwoColumn(vatExclusive, vatRate);

    this.setContext({ patient: patient, user: user, group: group, provider: null });

    // Process transactions for line items
    const allLineItems: any[] = [];
    transactions.forEach((transaction: any) => {
      if (transaction.PatientBillingLineItems && transaction.PatientBillingLineItems.length > 0) {
        transaction.PatientBillingLineItems.forEach((item: any) => {
          // Determine if this is a discount credit receipt
          const paymentMethod = transaction.PatientPayment?.paymentMethod || '';
          const isDiscountCredit = paymentMethod && 
            paymentMethod.toLowerCase().includes('credit:') &&
            (paymentMethod.toLowerCase().includes('discounted') || 
             paymentMethod.toLowerCase().includes('pro bono') ||
             paymentMethod.toLowerCase().includes('patient balance') ||
             paymentMethod.toLowerCase().includes('bad debt') ||
             paymentMethod.toLowerCase().includes('refund') ||
             paymentMethod.toLowerCase().includes('other'));
          
          allLineItems.push({
            ...item,
            documentType: transaction.documentType,
            billingNumber: transaction.billingNumber,
            issueDate: transaction.issueDate,
            createdAt: transaction.createdAt || transaction.issueDate, // Use createdAt for chronological sorting
            date: transaction.issueDate,
            payment: transaction.PatientPayment,
            documentProvider: transaction.Provider,
            // Add sort priority: invoices first (0), regular payments second (1), discount credits last (2)
            sortPriority: transaction.documentType === 'invoice' ? 0 : 
                         (transaction.documentType === 'receipt' && !isDiscountCredit) ? 1 : 2
          });
        });
      }
    });

    // --- SORT lineItems: Maintain existing order, then sort by billing document creation time ---
    const isPhysiotherapyPractice = group?.groupSpeciality === 'Physiotherapy';
    
    let sortedLineItems;
    if (isPhysiotherapyPractice) {
      // For physiotherapy practices: maintain displayIndex order, then sort by billing document creation time
      sortedLineItems = _.orderBy(allLineItems, [
        (item) => item.isModifierLine ? 1 : 0, // First sort by isModifierLine (0 comes before 1)
        (item) => item.displayIndex !== undefined ? item.displayIndex : 999, // Then by displayIndex (maintain existing order)
        (item) => item.createdAt ? moment(item.createdAt).valueOf() : (item.date ? moment(item.date).valueOf() : 0), // Then by billing document creation time
        (item) => item.sortPriority !== undefined ? item.sortPriority : 999, // Then by document type priority (invoice, payment, discount)
        (item) => -(item.units || item.quantity || 0), // Then by units descending
        (item) => item.codeType === 'consumable' ? 1 : 0 // Finally ensure consumables are last
      ]);
    } else {
      // For other practices: maintain codeType order, then sort by billing document creation time
      sortedLineItems = _.orderBy(allLineItems, [
        (item) => item.isModifierLine ? 1 : 0, // First sort by isModifierLine (0 comes before 1)
        (item) => item.codeType === 'consumable' ? 1 : 0, // Then by codeType (procedures first, consumables last) - maintain existing order
        (item) => item.createdAt ? moment(item.createdAt).valueOf() : (item.date ? moment(item.date).valueOf() : 0), // Then by billing document creation time
        (item) => item.sortPriority !== undefined ? item.sortPriority : 999, // Then by document type priority (invoice, payment, discount)
        (item) => -(item.units || item.quantity || 0) // Then by units descending
      ]);
    }

    // Group line items by date ONLY (not by document type or billing number)
    // Maintain sorted order within each date group
    const groupedItems: { [key: string]: any[] } = sortedLineItems.reduce((acc: { [key: string]: any[] }, item: any) => {
      const date = moment(item.date).format('DD/MM/YYYY');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(item);
      return acc;
    }, {});

    // Sort date groups chronologically (oldest first)
    const sortedDateKeys = Object.keys(groupedItems).sort((a, b) => {
      return moment(a, 'DD/MM/YYYY').valueOf() - moment(b, 'DD/MM/YYYY').valueOf();
    });
    
    // Rebuild groupedItems with sorted dates and ensure items within each group maintain order
    const sortedGroupedItems: { [key: string]: any[] } = {};
    sortedDateKeys.forEach(date => {
      sortedGroupedItems[date] = groupedItems[date]; // Items are already sorted from sortedLineItems
    });

    // --- merge duplicates in each date bucket ---
    // For physiotherapy practices: DO NOT merge items with same tariff code (keep all items separate)
    // For other practices: merge items with the same code + unit-price
    Object.keys(sortedGroupedItems).forEach(key => {
      const items = sortedGroupedItems[key];
      
      if (isPhysiotherapyPractice) {
        // For physiotherapy practices, do not merge - keep all items separate
        // Just re-sort to maintain order
        sortedGroupedItems[key] = _.orderBy(items, [
          (item) => item.isModifierLine ? 1 : 0,
          (item) => item.displayIndex !== undefined ? item.displayIndex : 999,
          (item) => item.createdAt ? moment(item.createdAt).valueOf() : (item.date ? moment(item.date).valueOf() : 0),
          (item) => item.sortPriority !== undefined ? item.sortPriority : 999
        ]);
      } else {
        // For other practices, merge items with the same code + unit-price
        const merged = Object.values(
          items.reduce((acc: Record<string, any>, item: any) => {
            const qty = item.quantity || 0;
            const total = item.totalAmount || item.balance || 0;
            // compute unit-price (guards against qty===0)
            const unitPrice = qty > 0 ? total / qty : 0;
            const mergeKey = `${item.code}-${unitPrice.toFixed(2)}`;

            if (!acc[mergeKey]) {
              // clone first occurrence
              acc[mergeKey] = { ...item };
            } else {
              // bump up qty and total
              acc[mergeKey].quantity = lineQuantity(acc[mergeKey].quantity + qty);
              acc[mergeKey].totalAmount += total;
              acc[mergeKey].balance += total;
            }
            return acc;
          }, {})
        );

        // Re-sort merged items to maintain order after merging
        const mergedArray = merged as any[];
        sortedGroupedItems[key] = _.orderBy(mergedArray, [
          (item) => item.isModifierLine ? 1 : 0,
          (item) => item.codeType === 'consumable' ? 1 : 0,
          (item) => item.createdAt ? moment(item.createdAt).valueOf() : (item.date ? moment(item.date).valueOf() : 0),
          (item) => item.sortPriority !== undefined ? item.sortPriority : 999
        ]);
      }
    });

    // 2) Calculate financials
    /* const statementTotalDue = Number(totalDue) || 0;
    const statementVat = Number(vat) || 0;
    const statementSubTotal = Number(subTotal) || 0; */

    // 3) Init jsPDF
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
    const margin = 10;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    // 4) No QR code for statements
    // Removed QR code generation as requested

    // 5) Fetch images & footer text
    const logoImage = await this.getBase64ImageFromURL(group?.headerImageUrl);
    const footerImage = group?.footerImageUrl
      ? await this.getBase64ImageFromURL(group.footerImageUrl)
      : null;
    const now = formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss', 'en-US');
    const footerText = `Generated by ${user.title} ${user.name} ${user.surname} on ${now}`;

    // 6) Build header/footer handlers
    const headerHeight = 35;
    const footerHeight = 10;
    const headerFunction = await this.createHeaderFunction(doc, logoImage, headerHeight);
    const footerFunction = await this.createFooterFunction(doc, null, footerText, footerHeight);

    // 7) Draw header
    await headerFunction();

    // 8) Safe content area
    const safeTop = headerHeight + 10;
    const safeBottom = pageH - footerHeight - margin;
    let y = safeTop;

    // 9) Title & meta
    doc.setFont('helvetica', 'bold').setFontSize(14);
    doc.text('ACCOUNT STATEMENT', pageW / 2, y, { align: 'center' });
    y += 8;

    // Calculate column widths and positions
    const colWidth = (pageW - (margin * 2)) / 3;

    // Title & Meta Table
    const isSameDay = moment(startDate).format('DD/MM/YYYY') == moment(endDate).format('DD/MM/YYYY');
    const dateRange = isSameDay ? `${moment(startDate).format('DD/MM/YYYY')} (00:00 - 23:59)` : `${moment(startDate).format('DD/MM/YYYY')} - ${moment(endDate).format('DD/MM/YYYY')}`;

    autoTable(doc, {
      startY: y,
      body: [
        [
          { content: `Statement Number: ${billingNumber}`, styles: { halign: 'left' } },
          { content: `Period: ${dateRange}`, styles: { halign: 'center' } },
          { content: `Generated: ${moment().format('DD/MM/YYYY')}`, styles: { halign: 'right' } }
        ]
      ],
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 0,
        lineColor: [255, 255, 255],
        lineWidth: 0
      },
      columnStyles: {
        0: { cellWidth: colWidth },
        1: { cellWidth: colWidth },
        2: { cellWidth: colWidth }
      },
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2
    });

    y += 10;

    // 10) Two-column panel & divider
    const panelH = 45;
    doc.setDrawColor(200)
      .line(pageW / 2, y, pageW / 2, y + panelH);

    // Patient and Practice Info Table
    autoTable(doc, {
      startY: y,
      body: this.createDocumentDetailsHeaderRow(patient, practiceInfo, group, vatRate),
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 1,
        lineColor: [255, 255, 255],
        lineWidth: 0,
        minCellHeight: 6
      },
      columnStyles: {
        0: { cellWidth: 30, halign: 'left' },  // Label
        1: { cellWidth: 60, halign: 'left' },  // Value
        2: { cellWidth: 30, halign: 'left', cellPadding: { left: 10, right: 0, top: 0, bottom: 0 } },  // Label with extra left padding
        3: { cellWidth: 60, halign: 'left', cellPadding: { left: 0, right: 0, top: 0, bottom: 0 } }   // Value
      },
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2
    });

    y += panelH + 5;

    // 11) Items table
    // Create table body with date headers
    const tableBody = Object.entries(groupedItems).flatMap(([date, items]) => {
      // Add document header row with custom styling
      const headerRow = this.createPatientAndProviderRow(date, patient, '', group);

      // Add items for this date
      const itemRows = items.map((i: any) => {

        const icdCodes = i.icdCodes && i.icdCodes?.length > 0 ? i.icdCodes.map((code: any) => code.code).join(', ') : '-';
        const pmbCodes = i.icdCodes && i.icdCodes?.length > 0
          ? i.icdCodes
            .flatMap((code: any) => code.pmbCodes || [])
            .map((pmb: any) => pmb.code)
            .filter((code: string) => code)
            .join(', ')
          : '-';
        const modifiers = i.modifiers && i.modifiers.length > 0 ? i.modifiers.join(', ') : '-';

        // Track if this is a credit event (not an actual payment)
        let isCreditEvent = false;
        
        if (i.documentType === 'receipt') {
          const paymentMethod = i.payment?.paymentMethod || '';
          const method = paymentMethod.toLowerCase();
          
          // Check if it's a credit type payment
          if (method.includes('credit:')) {
            isCreditEvent = true;
            // Replace "Payment received" with appropriate credit message
            if (method.includes('pro bono')) {
              i.description = i.description.replace('Payment received', 'Pro bono credit applied');
            } else if (method.includes('patient balance')) {
              i.description = i.description.replace('Payment received', 'Patient balance credit applied');
            } else if (method.includes('bad debt')) {
              i.description = i.description.replace('Payment received', 'Bad debt written off');
            } else if (method.includes('discounted')) {
              i.description = i.description.replace('Payment received', 'Discount credit applied');
            } else if (method.includes('refund')) {
              i.description = i.description.replace('Payment received', 'Refund credit applied');
            } else if (method.includes('other')) {
              i.description = i.description.replace('Payment received', 'Credit applied');
            } else {
              i.description = i.description.replace('Payment received', 'Credit applied');
            }
            // Add payment method in parentheses for credits
            i.description = i.description + (paymentMethod ? ` (${paymentMethod})` : '');
          } else {
            // Regular payment - add payment method via
            i.description = i.description + (paymentMethod ? ` via ${paymentMethod}` : '');
          }
        }
        // Format the details string
        const quantityStr = i.documentType === 'invoice' ? formatLineQuantity(i) : '';
        
        // For credit events, don't include the "PAYMENT -" prefix
        const codePrefix = (i.documentType === 'receipt' && isCreditEvent) 
          ? '' 
          : `${i.code || '-'} ${quantityStr ? `x ${quantityStr}` : ''} - `;
        
        const details = [
          `${codePrefix}${this.truncateDescription(i.description || '-', 150)}`,
        ]

        if (i.documentType === 'invoice') {
          if (modifiers !== '-') {
            details.push(`Modifiers: ${modifiers}`);
          }

          if (i.startTime && i.endTime) {
            details.push(`Time: ${i.startTime} - ${i.endTime}`);
          }

          if (i.weight || i.height || i.BMI) {
            details.push(`Weight: ${i.weight || '-'}kg, Height: ${i.height || '-'}cm, BMI: ${i.BMI || '-'}`);
          }

          const usingRule = i.ruleTypeOverride || i.ruleType || 'OH';
          const ruleTypeLabel = RULE_TYPE_LABELS[usingRule];
          const hospitalName = i.hospitalName && usingRule.indexOf('IH') > -1 ? i.hospitalName : '';
          details.push(`Place of service: ${ruleTypeLabel} ${hospitalName ? ' - ' + hospitalName : ''}`,)

          const lineProviderName = providerLabel(lineProvider(i, i.documentProvider));
          if (lineProviderName) {
            details.push(`Provider: ${lineProviderName}`);
          }

          if (icdCodes !== '-') {
            details.push(`ICD-10 Codes: ${icdCodes}`);
          }

          if (pmbCodes && pmbCodes !== '-') {
            details.push(`PMB Codes: ${pmbCodes}`);
          }
        }


        const detailsString = details.join('\n');

        // Statement rows for receipts render as a negative amount (`- R..`); preserve that sign
        // even in vatExclusive mode so the payment row keeps its inclusive figure and green styling.
        const inclusiveAmount = Number(i.totalAmount || i.balance || 0);
        if (i.documentType === 'receipt') {
          if (twoColumn) {
            return [detailsString, `- R${inclusiveAmount.toFixed(2)}`];
          }
          return [detailsString, formatVatPercent(vatRate), `- R${inclusiveAmount.toFixed(2)}`];
        }
        return this.formatItemRow(detailsString, inclusiveAmount, vatExclusive, vatRate);
      });

      return [...headerRow, ...itemRows];
    });

    const statementAmountColIndex = twoColumn ? 1 : 2;
    autoTable(doc, {
      startY: y,
      head: this.getItemsTableHead(vatExclusive, vatRate),
      body: tableBody,
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
        fillColor: [255, 255, 255],
        lineColor: [255, 255, 255],
        lineWidth: 0
      },
      headStyles: {
        fillColor: [225, 225, 225],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center'
      },
      didParseCell: (data) => {
        // only look at the body section, and only the first column (Details)
        if (data.section === 'body' && data.column.index === 0) {
          // join cell.text (an array of lines) into one string
          const fullText = data.cell.text.join(' ');
          if (fullText.includes('PAYMENT')) {
            // set the RGB textColor to green
            data.cell.styles.textColor = [0, 128, 0];
            data.cell.styles.fontStyle = 'bold';
          }
        }
        if (data.section === 'body' && data.column.index === statementAmountColIndex) {
          // join cell.text (an array of lines) into one string
          const fullText = data.cell.text.join(' ');
          if (fullText.includes('- R')) {
            // set the RGB textColor to green
            data.cell.styles.textColor = [0, 128, 0];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      columnStyles: this.getItemsColumnStyles(vatExclusive, 30, vatRate),
      margin: { top: headerHeight + 10, bottom: footerHeight + 10, left: margin, right: margin },
      tableWidth: pageW - margin * 2,
      didDrawPage: data => { y = data.cursor!.y; }
    });

    // --- Before rendering aging summary, check space ---
    const agingTableHeight = 20; // Approximate height of aging table
    const agingBankingTableHeight = 13;
    const buffer = 10;
    const totalBottomHeight = agingTableHeight + agingBankingTableHeight + buffer;

    if (safeBottom - y < totalBottomHeight) {
      doc.addPage();
      y = safeTop;
    }

    // --- Render aging summary after items table ---
    if (agingSummary) {
      y += 5; // Add some spacing after items table

      // Aging Summary Table - Full width with horizontal layout
      autoTable(doc, {
        startY: y,
        head: [
          [
            { content: '120+', styles: { fontStyle: 'bold', halign: 'center' } },
            { content: '90', styles: { fontStyle: 'bold', halign: 'center' } },
            { content: '60', styles: { fontStyle: 'bold', halign: 'center' } },
            { content: '30', styles: { fontStyle: 'bold', halign: 'center' } },
            { content: 'Current', styles: { fontStyle: 'bold', halign: 'center' } }
          ]
        ],
        body: [
          [
            { content: `R${agingSummary.days120Plus.toFixed(2)}`, styles: { halign: 'center' } },
            { content: `R${agingSummary.days90.toFixed(2)}`, styles: { halign: 'center' } },
            { content: `R${agingSummary.days60.toFixed(2)}`, styles: { halign: 'center' } },
            { content: `R${agingSummary.days30.toFixed(2)}`, styles: { halign: 'center' } },
            { content: `R${agingSummary.current.toFixed(2)}`, styles: { halign: 'center' } }
          ]
        ],
        theme: 'plain',
        styles: {
          font: 'helvetica',
          fontSize: 8,
          cellPadding: 3,
          overflow: 'linebreak',
          fillColor: [255, 255, 255],
          lineColor: [255, 255, 255],
          lineWidth: 0
        },
        headStyles: {
          fillColor: [225, 225, 225],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          halign: 'center'
        },
        columnStyles: {
          0: { cellWidth: (pageW - margin * 2) / 5 },
          1: { cellWidth: (pageW - margin * 2) / 5 },
          2: { cellWidth: (pageW - margin * 2) / 5 },
          3: { cellWidth: (pageW - margin * 2) / 5 },
          4: { cellWidth: (pageW - margin * 2) / 5 }
        },
        margin: { left: margin, right: margin },
        tableWidth: pageW - margin * 2,
        didDrawPage: data => { y = data.cursor!.y; }
      });
    }

    // --- Banking details heading and table just above the footer ---
    const bankingTableHeight = 13;
    const bankingY = safeBottom - bankingTableHeight;
    doc.setFont('helvetica', 'bold').setFontSize(9);
    doc.text('Banking Details', margin, bankingY - 2);
    const banking = group.bankingDetails || {};
    autoTable(doc, {
      startY: bankingY,
      body: [
        [
          { content: 'Account name:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.accountName || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Bank:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.bankName || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Branch:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.branchName || 'N/A', styles: { halign: 'left', fontSize: 8 } }
        ],
        [
          { content: 'Branch code:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.branchCode || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Account no.:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.accountNumber || 'N/A', styles: { halign: 'left', fontSize: 8 } },
          { content: 'Account type:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: banking.accountType || 'N/A', styles: { halign: 'left', fontSize: 8 } }
        ],
        [
          { content: 'Payment reference:', styles: { fontStyle: 'bold', halign: 'left', fontSize: 8 } },
          { content: `${patient.patientNumber || patient.idNumber || 'N/A'}`, styles: { halign: 'left', fontSize: 8 } },
          '', '', '', ''
        ]
      ],
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 0.5,
        lineColor: [255, 255, 255],
        lineWidth: 0,
        minCellHeight: 4,
        valign: 'top',
        overflow: 'linebreak',
      },
      columnStyles: {
        0: { cellWidth: (pageW - margin * 2) / 6 },
        1: { cellWidth: (pageW - margin * 2) / 6 },
        2: { cellWidth: (pageW - margin * 2) / 6 },
        3: { cellWidth: (pageW - margin * 2) / 6 },
        4: { cellWidth: (pageW - margin * 2) / 6 },
        5: { cellWidth: (pageW - margin * 2) / 6 },
      },
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2,
      didDrawCell: (data) => {
        data.cell.styles.fillColor = [225, 225, 225]; // subtle light gray
      }
    });

    // 16) Render header/footer on every page
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      await headerFunction();
      await footerFunction(i, totalPages);
    }

    return new Uint8Array(doc.output('arraybuffer'));
  }

  //Ensure payment code is unique
  createUniqueCode(): string {
    const now = new Date();
    const pad2 = (num: number) => num.toString().padStart(2, '0');

    // Full date components (no stripping zeros)
    const year = now.getFullYear().toString().slice(2);
    const month = pad2(now.getMonth() + 1);
    const day = pad2(now.getDate());
    const hours = pad2(now.getHours());
    const mins = pad2(now.getMinutes());
    const secs = pad2(now.getSeconds());

    const ms = now.getMilliseconds().toString().padStart(3, '0');

    return year + month + day + hours + mins + secs + ms;
  }

  private async createHeaderFunction(doc: jsPDF, logoImage: string | null, headerHeight: number): Promise<() => Promise<void>> {
    return () => {
      return new Promise<void>((resolve) => {
        if (logoImage) {
          const logoHeight = headerHeight;
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageCenter = pageWidth / 2;
          const img = new Image();
          img.onload = () => {
            const aspectRatio = img.width / img.height;
            const logoWidth = logoHeight * aspectRatio;
            const imgX = pageCenter - logoWidth / 2;
            doc.addImage(img, 'PNG', imgX, 5, logoWidth, logoHeight);
            resolve();
          };
          img.src = logoImage.indexOf('data:') === -1 ? `data:image/png;base64,${logoImage}` : logoImage;
        } else {
          resolve();
        }
      });
    };
  }

  private createPatientAndProviderRow(date: string, patient: any, providerName: string, group: any, provider?: any) {
    return [
      [
        {
          content: `${date} - Patient: ${patient.name} ${patient.surname} (${patient.dob})${providerName ? ` - Provider: ${providerName} ${group.practiceNumber || ''} ${provider?.mpNumber || ''}` : ''}`,
          styles: { fontStyle: 'bold' as const, fontSize: 9 }
        },
        '',
        ''
      ]
    ];
  }

  // vatRate is passed so a non-vendor practice shows no VAT number at all. The row itself
  // stays in place with a blank practice half — the table is a fixed 4-column grid, so
  // removing the row would shift every patient-side label (DOB, Medical Aid, ...) up one.
  private createDocumentDetailsHeaderRow(patient: any, practiceInfo: any, group: any, vatRate: number = DEFAULT_VAT_RATE): any[] {
    return [
      // Patient Info
      [
        { content: 'Name:', styles: { fontStyle: 'bold' } },
        { content: `${this.toTitleCase(patient.name)} ${this.toTitleCase(patient.surname)}` },
        { content: 'Practice:', styles: { fontStyle: 'bold' } },
        { content: `${this.toTitleCase(practiceInfo.practiceName)}` }
      ],
      [
        { content: 'ID/Passport:', styles: { fontStyle: 'bold' } },
        { content: patient.idNumber || patient.passportNumber || 'N/A' },
        { content: 'Reg. No.:', styles: { fontStyle: 'bold' } },
        { content: group.registrationNumber || 'N/A' }
      ],
      [
        { content: 'DOB:', styles: { fontStyle: 'bold' } },
        { content: patient.dob || 'N/A' },
        ...(vatRate > 0
          ? [{ content: 'VAT No.:', styles: { fontStyle: 'bold' } }, { content: group.vatNumber || 'N/A' }]
          : [{ content: '' }, { content: '' }])
      ],
      [
        { content: 'Medical Aid:', styles: { fontStyle: 'bold' } },
        { content: medicalAidDisplay(patient).name || 'Private' },
        { content: 'Practice No.:', styles: { fontStyle: 'bold' } },
        { content: group.practiceNumber || 'N/A' }
      ],
      [
        { content: 'Plan:', styles: { fontStyle: 'bold' } },
        { content: medicalAidDisplay(patient).plan || 'N/A' },
        { content: 'Tel:', styles: { fontStyle: 'bold' } },
        { content: group.accountsPhone || group.practicePhone || 'N/A' }
      ],
      [
        { content: 'Member No:', styles: { fontStyle: 'bold' } },
        { content: patient.medicalAidNumber ? (patient.medicalAidNumber + (patient.medicalDependantCode ? ` (Dep: ${patient.medicalDependantCode})` : '')) : 'N/A' },
        { content: 'Email:', styles: { fontStyle: 'bold' } },
        { content: group.accountsEmail || group.practiceEmail || 'N/A' }
      ],
      [
        { content: 'Address:', styles: { fontStyle: 'bold' } },
        { content: patient.address || 'N/A' },
        { content: 'Address:', styles: { fontStyle: 'bold' } },
        { content: group.address || 'N/A' }
      ]
    ]
  }

  private async createFooterFunction(doc: jsPDF, footerImage: string | null, footerText: string, footerHeight: number): Promise<(pageNumber: number, totalPages: number) => Promise<void>> {
    return (pageNumber: number, totalPages: number) => {
      return new Promise<void>((resolve) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageCenter = pageWidth / 2;
        const footerTextY = pageHeight - 5;
        const pageNumY = pageHeight - 5;

        if (footerImage) {
          const footerImageHeight = 35;
          const img = new Image();
          img.onload = () => {
            const aspectRatio = img.width / img.height;
            const footerImageWidth = footerImageHeight * aspectRatio;
            const imgX = pageCenter - footerImageWidth / 2;
            const imgY = pageHeight - footerHeight;
            doc.addImage(img, 'PNG', imgX, imgY, footerImageWidth, footerImageHeight);
            doc.setFontSize(8);
            doc.text(footerText, 10, footerTextY);
            doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - 10, pageNumY, { align: 'right' });
            resolve();
          };
          img.src = footerImage.indexOf('data:') === -1 ? `data:image/png;base64,${footerImage}` : footerImage;
        } else {
          doc.setFontSize(8);
          doc.text(footerText, 10, footerTextY);
          doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - 10, pageNumY, { align: 'right' });
          resolve();
        }
      });
    };
  }

  private async getBase64ImageFromURL(url: string): Promise<string> {
    if (!url) {
      url = 'assets/full-logo.jpg';
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } else {
      const proxyUrl = `${environment.API_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
      const response = await fetch(proxyUrl);
      const data = await response.json();
      return data.base64Image;
    }
  }

  private async generateYocoCheckoutUrl(amountInCents: number, billingDocumentId?: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.yocoService.generatePaymentUrl(
        amountInCents / 100, // Convert back to rand
        this.contextGroup.id,
        billingDocumentId
      ).subscribe({
        next: (url) => resolve(url),
        error: (err) => reject(err)
      });
    });
  }


  // Title Case Converter
  private toTitleCase(str: string): string {
    if (!str) return str;
    return str.toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }



  // Helper function to truncate descriptions
  private truncateDescription(description: string, maxLength: number): string {
    if (!description || description.length <= maxLength) {
      return description || '-';
    }
    return description.slice(0, maxLength - 3) + '...';
  }
}
